import { useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { CheckCircle, Close, HelpOutline, InfoOutlined, Refresh } from '@mui/icons-material';
import { Warning } from '@mui/icons-material/';
import { Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { stringUtilities } from 'utils';
import { useStore } from 'hooks';

import TroubleshootModal from '../TroubleshootModal';

const SystemOfflineBanner = ({
  closeBanner,
  refreshTimeLeft,
  handleRefresh,
  isRefreshing,
  onlineCountdown,
  isBackOnline,
  handleDetailsClick,
}) => {
  const { t } = useTranslation();
  const { hardwareStatusStore } = useStore();

  const [troubleshootingModalOpen, setTroubleshootingModalOpen] = useState(false);

  const status = useMemo(() => {
    if (hardwareStatusStore.isRestored) return t('shared.SystemOffline.systemConnectivityRestored');
    if (hardwareStatusStore.isFullyOffline) return t('shared.SystemOffline.systemOffline');
    return t('shared.SystemOffline.partialSystemOffline');
  }, [t, hardwareStatusStore.isRestored, hardwareStatusStore.isFullyOffline]);

  return (
    <>
      <StyledRootDiv className={clsx({ restored: hardwareStatusStore.isRestored })}>
        <div className={'menuOptions'}>
          <span className={'menuOption'}>
            {hardwareStatusStore.isRestored ? (
              <CheckCircle />
            ) : (
              <Warning sx={{ width: '24px' }} htmlColor={'#000'} />
            )}
            <Typography variant={'h6'} className={'menuOptionText systemOfflineText'}>
              {status}
            </Typography>
          </span>
          |
          {hardwareStatusStore.isRestored ? (
            <span className={'menuOption'}>
              <span className={'menuText'} onClick={closeBanner}>
                <Close />
                {t('shared.SystemOffline.close')}
              </span>
              <span className={clsx('timer online', { active: isBackOnline })}>
                {stringUtilities.formatTimer(onlineCountdown)}
              </span>
            </span>
          ) : (
            <>
              <span className={'menuOption'} onClick={() => setTroubleshootingModalOpen(true)}>
                <HelpOutline htmlColor={'#000'} />
                <Typography variant={'h6'} className={'menuOptionText'}>
                  {t('shared.SystemOffline.troubleshooting')}
                </Typography>
              </span>
              |
              <span className={'menuOption'} onClick={handleDetailsClick}>
                <InfoOutlined htmlColor={'#000'} />
                <Typography variant={'h6'} className={'menuOptionText'}>
                  {t('shared.SystemOffline.details')}
                </Typography>
              </span>
              |
              <Tooltip
                title={isRefreshing ? t('shared.SystemOffline.systemRequiresTimeToRestart') : ''}
              >
                <span className={'menuOption'} onClick={handleRefresh}>
                  <Refresh htmlColor={isRefreshing ? 'rgba(0,0,0,0.5)' : '#000'} />
                  <Typography
                    variant={'h6'}
                    className={clsx('menuOptionText', { refreshing: isRefreshing })}
                  >
                    {t('shared.SystemOffline.refresh')}
                  </Typography>
                  <span className={clsx('timer refresh', { active: isRefreshing })}>
                    {stringUtilities.formatTimer(refreshTimeLeft)}
                  </span>
                </span>
              </Tooltip>
            </>
          )}
        </div>
      </StyledRootDiv>
      <TroubleshootModal
        open={troubleshootingModalOpen}
        onClose={() => setTroubleshootingModalOpen(false)}
      />
    </>
  );
};

const StyledRootDiv = styled('div')(({ theme: { palette } }) => ({
  height: '40px',
  backgroundColor: palette.status.red,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&.restored': {
    backgroundColor: palette.status.green,
  },
  '.menuOptions': {
    display: 'flex',
    color: palette.black,
  },
  '.menuOption': {
    cursor: 'pointer',
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    gridColumnGap: '4px',
    alignItems: 'center',
    margin: '0 16px',
  },
  '.menuText': {
    display: 'flex',
    alignItems: 'center',
  },
  '.menuOptionText': {
    color: palette.black,
    '&.refreshing': {
      color: 'rgba(0,0,0,0.5)',
    },
    '&.systemOfflineText': {
      cursor: 'default',
    },
  },
  '.timer': {
    backgroundColor: palette.black,
    borderRadius: '10px',
    fontSize: '14px',
    lineHeight: '16px',
    padding: '0 8px',
    opacity: 0,
    transition: 'opacity 0.5s',
    width: '57px',
    '&.online': {
      color: palette.status.green,
    },
    '&.refresh': {
      color: palette.status.red,
    },
    '&.active': {
      opacity: 1,
    },
  },
}));

export default observer(SystemOfflineBanner);
